/**
 * Finds the first error path from a Formik errors object.
 * Supports nested errors.
 */
export const getFirstError = (
  errors: Record<string, any>,
  parent = '',
): string | null => {
  for (const key of Object.keys(errors)) {
    const value = errors[key];
    const path = parent ? `${parent}.${key}` : key;

    if (typeof value === 'string') {
      return path;
    }

    if (value && typeof value === 'object') {
      const nestedError = getFirstError(value, path);

      if (nestedError) {
        return nestedError;
      }
    }
  }

  return null;
};

/**
 * Maps an error field to its scroll section.
 * Falls back to the field name if no mapping exists.
 */
export const getErrorSectionId = (
  errorPath: string,
  sectionMap: Record<string, string>,
): string => {
  if (sectionMap[errorPath]) {
    return sectionMap[errorPath];
  }

  const parentPath = errorPath.split('.').slice(0, -1).join('.');

  if (parentPath && sectionMap[parentPath]) {
    return sectionMap[parentPath];
  }

  const field = errorPath.split('.').pop()!;

  return sectionMap[field] ?? field;
};

/**
 * Scrolls the form to the first error section.
 */
export const scrollToFirstErrorSection = (
  errors: Record<string, any>,
  sectionMap: Record<string, string>,
): void => {
  const firstErrorPath = getFirstError(errors);

  if (!firstErrorPath) return;

  const sectionId = getErrorSectionId(firstErrorPath, sectionMap);

  const element = document.getElementById(sectionId);

  if (!element) return;

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};
